import { LocationWizardData } from './types';
import { resolveManagedSpaces } from './locationUpdateHelpers';

export interface LocationDraftPayload {
  locationName: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  addressLatitude: number | null;
  addressLongitude: number | null;
  /**
   * True only when the user explicitly chose "Your entry" over Google's
   * suggestion in the address-confirmation modal. Sent on every save so the
   * backend knows whether the address was user-verified. Matches the
   * `addressUserAsserted` field on `LocationCreateVO`/`LocationUpdateVO`.
   */
  addressUserAsserted: boolean;
  phoneNumber: string;
  listingImageFileName: string;
  /** Only present when creating a new draft (ties the record to the supplier). */
  account?: { id: string };
  /** Only present when creating a new draft; updates intentionally omit it so an
   *  existing location's publish status is preserved until Step 5 (Pricing). */
  live?: boolean;
  /** Only present on updates — serializes managedSpaces as a positive value for
   *  managed locations and 0 for unmanaged locations. */
  managedSpaces?: number;
  /** Only present on updates — the raw lifecycle status of the existing record
   *  (`"DRAFT"`, `"IN_REVIEW"`, `"APPROVED"`). The backend resets an omitted
   *  status back to `DRAFT` on PATCH, so we echo the current status to keep a
   *  Pending (IN_REVIEW) or Approved location in its existing stage while editing. */
  status?: string;
}

/**
 * Builds the request body for saving a location from the wizard's Step 1 data.
 *
 * - New record (no `data.id`) → POST /api/locations. Includes `account` and
 *   `live: false` so the draft is created unpublished and owned by the supplier.
 * - Existing record (has `data.id`) → PATCH /api/locations/{id}. Omits `live` and
 *   `account` so the current publish status / ownership is preserved, serializes
 *   `managedSpaces` according to the managed-location setting, and echoes the
 *   current `status` so the backend doesn't reset it to `DRAFT`.
 *
 * Address fields are nulled out when no address has been entered.
 *
 * @param data The current wizard form data
 * @param accountId The supplier account id (used only for new drafts)
 * @param listingImageFileName The (possibly newly uploaded) listing image file name
 * @param currentStatus The existing record's raw lifecycle status, echoed back on
 *        updates so a Pending/Approved location keeps its stage while editing.
 */
export function buildLocationDraftPayload(
  data: LocationWizardData,
  accountId: string | undefined,
  listingImageFileName: string,
  currentStatus?: string
): LocationDraftPayload {
  const isNew = !data.id;
  const hasAddress = Boolean(data.locationAddress && data.locationAddress.trim());

  const payload: LocationDraftPayload = {
    locationName: data.locationName,
    addressLine1: hasAddress ? data.addressLine1 : null,
    addressLine2: hasAddress ? data.addressLine2 : null,
    city: hasAddress ? data.city : null,
    state: hasAddress ? data.state : null,
    zip: hasAddress ? data.zip : null,
    addressLatitude: hasAddress ? data.addressLatitude : null,
    addressLongitude: hasAddress ? data.addressLongitude : null,
    addressUserAsserted: hasAddress ? data.addressManuallyConfirmed : false,
    phoneNumber: data.phoneNumber,
    listingImageFileName,
  };

  if (isNew) {
    payload.account = accountId ? { id: accountId } : undefined;
    payload.live = false;
  } else {
    // Keep the saved managed-space value aligned with the managed-location setting.
    payload.managedSpaces = resolveManagedSpaces(data);
    // Echo the current lifecycle status so the backend doesn't reset it to
    // DRAFT on PATCH — otherwise editing (or closing out of) a Pending
    // (IN_REVIEW) or Approved location would silently demote it to Draft.
    if (currentStatus) {
      payload.status = currentStatus;
    }
  }

  return payload;
}

